import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBoard } from "@/context/BoardContext";

export default function BoardBar() {
  const { items, count, remove, clear } = useBoard();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (count === 0) return null;

  const requestQuote = () => {
    setOpen(false);
    navigate("/contact?board=1");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-20 lg:bottom-6 right-4 lg:right-6 bg-secondary text-white pl-4 pr-5 py-3 rounded-full shadow-xl flex items-center gap-2 hover:-translate-y-1 transition-transform"
        data-testid="board-fab"
      >
        <span className="relative">
          <Heart className="h-5 w-5 fill-primary text-primary" />
          <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] font-bold h-4 min-w-4 px-1 rounded-full flex items-center justify-center">{count}</span>
        </span>
        <span className="font-bold text-sm hidden sm:inline">Inspiration Board</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 z-50 bg-black/50" />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed z-50 top-0 right-0 h-full w-full sm:w-[420px] bg-card border-l border-border flex flex-col"
              data-testid="board-drawer"
            >
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div>
                  <h3 className="font-heading text-xl font-extrabold flex items-center gap-2"><Heart className="h-5 w-5 fill-primary text-primary" /> Your Board</h3>
                  <p className="text-sm text-muted-foreground">{count} project{count !== 1 ? "s" : ""} saved</p>
                </div>
                <button onClick={() => setOpen(false)} data-testid="board-close"><X className="h-5 w-5" /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {items.map((it) => (
                  <div key={it.id} className="flex gap-3 bg-muted/50 p-2 group" data-testid={`board-item-${it.id}`}>
                    <img src={it.image} alt={it.title} className="h-16 w-20 object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{it.title}</p>
                      <p className="text-xs text-muted-foreground">{it.category}</p>
                    </div>
                    <button onClick={() => remove(it.id)} className="text-muted-foreground hover:text-destructive self-center" data-testid={`board-remove-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t border-border space-y-2">
                <Button onClick={requestQuote} className="w-full h-12 bg-primary hover:bg-primary/90 rounded-sm font-bold" data-testid="board-request-quote">
                  Request Quote With These <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <button onClick={clear} className="w-full text-sm text-muted-foreground hover:text-destructive font-medium" data-testid="board-clear">Clear board</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
